import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Searcher.css';
import { User, File, Search, Folder, Calendar, Database, Copy, Check } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useCurrentUserPermissions } from '../../features/spaces/hooks/useSpaceDashboard';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: limpia todos los resultados cuando el término de búsqueda se vacía y resetea el índice seleccionado al cambiar los resultados filtrados.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const getRelativeDateStr = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = d2 - d1;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'hoy';
    if (diffDays === 1) return 'ayer';
    if (diffDays >= 2 && diffDays <= 7) return `hace ${diffDays} días`;
    if (diffDays <= 14) return 'hace 1 semana';
    if (diffDays <= 21) return 'hace 2 semanas';
    if (diffDays <= 28) return 'hace 3 semanas';
    if (diffDays <= 35) return 'hace 4 semanas';
    if (diffDays < 60) return 'hace 1 mes';
    const months = Math.floor(diffDays / 30);
    return `hace ${months} meses`;
};

const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = date.getDate();
    const month = date.toLocaleString('es-ES', { month: 'long' });
    const year = date.getFullYear();
    const time = date.toLocaleString('es-ES', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day} ${month} ${year} • ${time}`;
};

const getTaskStatusColor = (stage) => {
    if (!stage) return '#6b7280';
    const s = stage.toLowerCase();
    if (s.includes('planeada')) return '#E9D92C';
    if (s.includes('proceso')) return '#3b82f6';
    if (s.includes('bloqueo') || s.includes('bloqueada')) return '#ef4444';
    if (s.includes('completada')) return '#22c55e';
    if (s.includes('synk')) return '#a855f7';
    return '#6b7280';
};

const Searcher = ({ onSelect }) => {
    const navigate = useNavigate();
    const { onlyMyEvents } = useSettings();
    const { permissions } = useCurrentUserPermissions();
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [copiedEmail, setCopiedEmail] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // Data Sources
    const [users, setUsers] = useState([]);
    const [docs, setDocs] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [databases, setDatabases] = useState([]);
    const [events, setEvents] = useState([]);

    // Filtered Lists
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [filteredDatabases, setFilteredDatabases] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);

    const wrapperRef = useRef(null);
    const scrollableRef = useRef(null);

    useEffect(() => {
        // `Searcher` vive montado siempre dentro de `MenuMedia.jsx` (la
        // barra superior, presente en toda la app) — antes este efecto solo dependía de
        // `[onlyMyEvents]`, así que corría en cada carga de página para cada usuario, sin
        // importar si alguien llegaba a abrir el buscador. Eso convertía `all=true` en un
        // fetch de facto "por defecto" (el problema exacto que se buscaba eliminar), solo
        // que ahora explícito en vez de silencioso. Se posterga el fetch hasta que el
        // usuario interactúa de verdad con el input (`showResults` se activa en `onFocus`/
        // `onClick`/`onChange`, más abajo en el JSX).
        if (!showResults) return;

        const fetchAll = async () => {
            try {
                let currentUser = {};
                try { currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch(e){}

                const todayStr = new Date().toISOString().split('T')[0];
                const eventParams = new URLSearchParams({
                    start: `${todayStr}T00:00:00`,
                    end: `${todayStr}T23:59:59`,
                    onlyMine: String(onlyMyEvents),
                });
                if (currentUser.id) eventParams.set('userId', currentUser.id);
                if (currentUser.nick) eventParams.set('userNick', currentUser.nick);

                // Identidad vía cookie de sesión, no header manual.
                const [userRes, docRes, taskRes, dbRes, eventRes] = await Promise.all([
                    fetch('/api/users?all=true', { credentials: 'include' }).then(r => r.json().catch(() => [])),
                    permissions?.doc_view !== false ? fetch('/api/docs', { credentials: 'include' }).then(r => r.json().catch(() => ({}))) : Promise.resolve({ success: true, docs: [] }),
                    // El buscador global necesita el dataset completo de
                    // tareas para indexar (no está acotado a un rango de fechas ni a un
                    // espacio) — se pide explícito con `all=true`, mismo criterio que
                    // `/api/users?all=true` un poco más arriba. Sin este flag, `/api/tasks`
                    // ya no devuelve el historial completo por default.
                    fetch('/api/tasks?all=true', { credentials: 'include' }).then(r => r.json().catch(() => ({}))),
                    // Bug encontrado y corregido (2026-07-08): esto pedía
                    // `GET /api/dataindex` (sin id), una ruta que nunca existió — 404 real en
                    // cada carga del buscador. `/api/databases/general` es el registro real
                    // de bases de datos (tabla `table_data_name`, ver
                    // src/dataindex/infrastructure/dataIndexRouter.js) y ya devuelve
                    // `{success, data}`, no `{success, indices}` como asumía este código.
                    fetch('/api/databases/general', { credentials: 'include' }).then(r => r.json().catch(() => ({ success: false }))),
                    fetch(`/api/events?${eventParams.toString()}`, { credentials: 'include' }).then(r => r.json().catch(() => ({})))
                ]);

                if (Array.isArray(userRes)) setUsers(userRes);
                if (docRes.success) setDocs(flattenDocs(docRes.docs || []));
                if (taskRes.success) setTasks(taskRes.tasks || []);
                if (dbRes.success && Array.isArray(dbRes.data)) {
                    setDatabases(dbRes.data);
                }
                if (eventRes.success) setEvents(eventRes.events || []);

            } catch (error) {
                console.error("Searcher Fetch Error:", error);
            }
        };
        fetchAll();
    }, [showResults, onlyMyEvents]);

    const flattenDocs = (nodes) => {
        let flat = [];
        nodes.forEach(node => {
            flat.push(node);
            if (node.children) flat = flat.concat(flattenDocs(node.children));
        });
        return flat;
    };

    useEffect(() => {
        if (!searchTerm || searchTerm.trim() === '') {
            setFilteredUsers([]);
            setFilteredDocs([]);
            setFilteredTasks([]);
            setFilteredDatabases([]);
            setFilteredEvents([]);
            setSelectedIndex(-1);
            return;
        }

        const lower = searchTerm.toLowerCase();

        setFilteredUsers(users.filter(u =>
            (u.nick && u.nick.toLowerCase().includes(lower)) ||
            (u.name && u.name.toLowerCase().includes(lower)) ||
            (u.email && u.email.toLowerCase().includes(lower))
        ));

        setFilteredTasks(tasks.filter(t =>
            (t.title && t.title.toLowerCase().includes(lower)) ||
            (t.assignee && t.assignee.toLowerCase().includes(lower))
        ));

        setFilteredDocs(permissions?.doc_view !== false ? docs.filter(d =>
            (d.title && d.title.toLowerCase().includes(lower))
        ) : []);

        setFilteredDatabases(databases.filter(db =>
            db.table_data_name && db.table_data_name.toLowerCase().includes(lower)
        ));

        setFilteredEvents(events.filter(e =>
            (e.title && e.title.toLowerCase().includes(lower)) ||
            (e.attendees && typeof e.attendees === 'string' && e.attendees.toLowerCase().includes(lower))
        ));

        setSelectedIndex(0);
    }, [searchTerm, users, docs, tasks, databases, events]);

    // Flatten results for keyboard navigation
    const flatResults = useMemo(() => {
        const results = [];
        if (activeFilter === 'Todos' || activeFilter === 'Users') {
            const limit = activeFilter === 'Todos' ? 5 : undefined;
            filteredUsers.slice(0, limit).forEach(u => results.push({ ...u, _navType: 'user' }));
        }
        if (activeFilter === 'Todos' || activeFilter === 'Tareas') {
            const limit = activeFilter === 'Todos' ? 5 : undefined;
            filteredTasks.slice(0, limit).forEach(t => results.push({ ...t, _navType: 'task' }));
        }
        if (activeFilter === 'Todos' || activeFilter === 'Documentos') {
            const limit = activeFilter === 'Todos' ? 5 : undefined;
            filteredDocs.slice(0, limit).forEach(d => results.push({ ...d, _navType: 'doc' }));
        }
        if (activeFilter === 'Todos' || activeFilter === 'Bases de Datos') {
            const limit = activeFilter === 'Todos' ? 5 : undefined;
            filteredDatabases.slice(0, limit).forEach(db => results.push({ ...db, _navType: 'database' }));
        }
        if (activeFilter === 'Todos' || activeFilter === 'Eventos') {
            const limit = activeFilter === 'Todos' ? 5 : undefined;
            filteredEvents.slice(0, limit).forEach(e => results.push({ ...e, _navType: 'event' }));
        }
        return results;
    }, [filteredUsers, filteredTasks, filteredDocs, filteredDatabases, filteredEvents, activeFilter]);

    // Adjust selected index if filter changes
    useEffect(() => {
        setSelectedIndex(flatResults.length > 0 ? 0 : -1);
    }, [activeFilter, flatResults]);

    // Ensure selected item is visible
    useEffect(() => {
        if (selectedIndex >= 0 && scrollableRef.current) {
            const selectedEl = scrollableRef.current.querySelector(`[data-index="${selectedIndex}"]`);
            if (selectedEl) {
                selectedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    const handleKeyDown = (e) => {
        if (!showResults) {
            if (e.key === 'Enter') setShowResults(true);
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(flatResults.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(0, prev - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
                const item = flatResults[selectedIndex];
                handleSelect(item, item._navType);
            }
        } else if (e.key === 'Escape') {
            setShowResults(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item, type) => {
        if (onSelect) onSelect(item, type);
        setShowResults(false);

        if (type === 'user') navigate(`/user/${item.id}`);
        else if (type === 'doc') navigate(`/docs/${item.id}`);
        else if (type === 'database') navigate(`/database/view/${item.iddata}`);
        // Ruta raíz `/<uuid>` (RootHandler en App.jsx) abre la tarea en el overlay standalone,
        // que trae solo esa tarea puntual sin depender del filtro de rango de la lista
        // (`/task/<id>` sin espacio/vista/rango caía en los defaults 'list'/'today' y podía no
        // encontrar la tarea si no estaba dentro de "hoy" para el espacio activo).
        else if (type === 'task') navigate(`/${item.id}`);
        else if (type === 'event') navigate(`/calendar`);
    };

    const copyEmail = (e, email) => {
        e.stopPropagation();
        if (email) {
            navigator.clipboard.writeText(email);
            setCopiedEmail(email);
            setTimeout(() => setCopiedEmail(null), 2000);
        }
    };

    const calculateIncompleteTasks = (user) => {
        const userNick = (user.nick || '').toLowerCase();
        const userIdStr = String(user.id);
        let count = 0;
        tasks.forEach(t => {
            if (!t.stage || ['completada', 'archivada', 'cerrada'].includes(t.stage.toLowerCase())) return;
            if (t.checked) return;

            const assignees = (t.assignee || '').toLowerCase();
            if (assignees.includes(userNick) || assignees.includes(userIdStr)) {
                count++;
            }
        });
        return count;
    };

    const parseAttendees = (attString) => {
        if (!attString) return [];
        try {
            const parsed = JSON.parse(attString);
            return Array.isArray(parsed) ? parsed : [];
        } catch(e) { return []; }
    };

    const hasResults = flatResults.length > 0;
    let globalIndex = 0;

    return (
        <div className="searcher-wrapper" ref={wrapperRef}>
            <div className="searcher-input-container">
                <input
                    type="text"
                    className="searcher-input"
                    placeholder="Search everywhere..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowResults(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowResults(true)}
                    onClick={() => setShowResults(true)}
                />
                <div className="searcher-icon">
                    <Search size={18} />
                </div>
            </div>

            {showResults && searchTerm && (
                <div className="searcher-results-box">
                    
                    {/* Top Filters */}
                    <div className="searcher-filters-bar">
                        <div 
                            className={`searcher-filter-tag ${activeFilter === 'Todos' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('Todos')}
                        >
                            Todos
                        </div>
                        <div 
                            className={`searcher-filter-tag ${activeFilter === 'Users' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('Users')}
                        >
                            Users <span className="filter-count">{filteredUsers.length}</span>
                        </div>
                        <div 
                            className={`searcher-filter-tag ${activeFilter === 'Tareas' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('Tareas')}
                        >
                            Tareas <span className="filter-count">{filteredTasks.length}</span>
                        </div>
                        {permissions?.doc_view !== false && (
                            <div 
                                className={`searcher-filter-tag ${activeFilter === 'Documentos' ? 'active' : ''}`}
                                onClick={() => setActiveFilter('Documentos')}
                            >
                                Documentos <span className="filter-count">{filteredDocs.length}</span>
                            </div>
                        )}
                        <div 
                            className={`searcher-filter-tag ${activeFilter === 'Bases de Datos' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('Bases de Datos')}
                        >
                            Bases de Datos <span className="filter-count">{filteredDatabases.length}</span>
                        </div>
                        <div 
                            className={`searcher-filter-tag ${activeFilter === 'Eventos' ? 'active' : ''}`}
                            onClick={() => setActiveFilter('Eventos')}
                        >
                            Eventos <span className="filter-count">{filteredEvents.length}</span>
                        </div>
                    </div>

                    <div className="searcher-results-scrollable" ref={scrollableRef}>
                        {/* Users */}
                        {(activeFilter === 'Todos' || activeFilter === 'Users') && filteredUsers.length > 0 && (
                            <div className="result-section">
                                <div className="section-label">Users</div>
                                {filteredUsers.slice(0, activeFilter === 'Todos' ? 5 : undefined).map((user) => {
                                    const currentIndex = globalIndex++;
                                    return (
                                        <div 
                                            key={user.id} 
                                            data-index={currentIndex}
                                            className={`search-result-item-detailed ${currentIndex === selectedIndex ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(user, 'user')}
                                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        >
                                            <div className="res-left">
                                                <div className="res-avatar">
                                                    {(user['link-profile-img-thumb'] || user['link-profile-img']) ? (
                                                        <img src={(user['link-profile-img-thumb'] || user['link-profile-img'])} alt={user.nick} loading="lazy" />
                                                    ) : (
                                                        <User size={18} />
                                                    )}
                                                </div>
                                                <div className="res-title">{user.nick || user.name}</div>
                                            </div>
                                            <div className="res-right">
                                                {user.email && (
                                                    <div className="res-email-box" onClick={(e) => copyEmail(e, user.email)}>
                                                        <span>{user.email}</span>
                                                        {copiedEmail === user.email ? <Check size={12} color="#4ade80" /> : <Copy size={12} />}
                                                    </div>
                                                )}
                                                <div className="res-role-tag">{user.level === 0 ? 'Admin' : 'Miembro'}</div>
                                                <div className="res-dark-tag">tareas {calculateIncompleteTasks(user)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Tareas */}
                        {(activeFilter === 'Todos' || activeFilter === 'Tareas') && filteredTasks.length > 0 && (
                            <div className="result-section">
                                <div className="section-label">Tareas</div>
                                {filteredTasks.slice(0, activeFilter === 'Todos' ? 5 : undefined).map((task) => {
                                    const currentIndex = globalIndex++;
                                    return (
                                        <div 
                                            key={task.id} 
                                            data-index={currentIndex}
                                            className={`search-result-item-detailed ${currentIndex === selectedIndex ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(task, 'task')}
                                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        >
                                            <div className="res-left">
                                                <div className="res-status-dot" style={{ backgroundColor: getTaskStatusColor(task.stage) }}></div>
                                                <span className="res-subtitle" style={{ color: getTaskStatusColor(task.stage), textTransform: 'capitalize', fontSize: '10px', fontWeight: 600 }}>{task.stage || 'Planeada'}</span>
                                                {task.assignee && <span className="res-assignee-text">@{task.assignee}</span>}
                                                <div className="res-title">{task.title || 'Sin título'}</div>
                                            </div>
                                            <div className="res-right">
                                                <div className="res-dark-tag" style={{ border: 'none', background: '#333' }}>
                                                    {getRelativeDateStr(task.created_at || task.date) || 'sin fecha'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Documentos */}
                        {(activeFilter === 'Todos' || activeFilter === 'Documentos') && filteredDocs.length > 0 && (
                            <div className="result-section">
                                <div className="section-label">Documentos</div>
                                {filteredDocs.slice(0, activeFilter === 'Todos' ? 5 : undefined).map((doc) => {
                                    const currentIndex = globalIndex++;
                                    return (
                                        <div 
                                            key={doc.id} 
                                            data-index={currentIndex}
                                            className={`search-result-item-detailed ${currentIndex === selectedIndex ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(doc, 'doc')}
                                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        >
                                            <div className="res-left">
                                                <div className="search-result-icon">
                                                    {doc.icon && doc.icon !== 'folder' && doc.icon !== 'file' && doc.icon !== 'carpeta' && doc.icon !== 'document' ? (
                                                        <span style={{ fontSize: '14px', lineHeight: '18px' }}>{doc.icon}</span>
                                                    ) : doc.icon === 'folder' || doc.icon === 'carpeta' ? (
                                                        <Folder size={18} />
                                                    ) : (
                                                        <File size={18} />
                                                    )}
                                                </div>
                                                <div className="res-title">{doc.title || doc.name}</div>
                                            </div>
                                            <div className="res-right">
                                                <div className="res-dark-tag" style={{ border: 'none', background: '#333' }}>
                                                    creado {getRelativeDateStr(doc.created_at) || 'sin fecha'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Bases de Datos */}
                        {(activeFilter === 'Todos' || activeFilter === 'Bases de Datos') && filteredDatabases.length > 0 && (
                            <div className="result-section">
                                <div className="section-label">Bases de Datos</div>
                                {filteredDatabases.slice(0, activeFilter === 'Todos' ? 5 : undefined).map((db) => {
                                    const currentIndex = globalIndex++;
                                    return (
                                        <div 
                                            key={db.iddata} 
                                            data-index={currentIndex}
                                            className={`search-result-item-detailed ${currentIndex === selectedIndex ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(db, 'database')}
                                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        >
                                            <div className="res-left">
                                                <div className="search-result-icon"><Database size={18} /></div>
                                                <div className="res-title">{db.table_data_name || "Untitled Database"}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Eventos */}
                        {(activeFilter === 'Todos' || activeFilter === 'Eventos') && filteredEvents.length > 0 && (
                            <div className="result-section">
                                <div className="section-label">Eventos</div>
                                {filteredEvents.slice(0, activeFilter === 'Todos' ? 5 : undefined).map((event) => {
                                    const currentIndex = globalIndex++;
                                    const parsedAttendees = parseAttendees(event.attendees);
                                    return (
                                        <div 
                                            key={event.id} 
                                            data-index={currentIndex}
                                            className={`search-result-item-detailed ${currentIndex === selectedIndex ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(event, 'event')}
                                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                                        >
                                            <div className="res-left">
                                                <div className="search-result-icon" style={{color: '#ef4444'}}><Calendar size={18} /></div>
                                                {parsedAttendees.length > 0 && (
                                                    <div className="res-attendees-mini">
                                                        {parsedAttendees.slice(0, 2).map((a, i) => (
                                                            <img key={i} src={a.picture || `https://ui-avatars.com/api/?name=${a.nick || 'U'}&background=random`} alt="u" loading="lazy" />
                                                        ))}
                                                        {parsedAttendees.length > 2 && <span>+{parsedAttendees.length - 2}</span>}
                                                    </div>
                                                )}
                                                <div className="res-title" style={{marginLeft: parsedAttendees.length > 0 ? '6px' : '0'}}>{event.title}</div>
                                            </div>
                                            <div className="res-right">
                                                <div className="res-dark-tag" style={{ border: 'none', background: '#333' }}>
                                                    {formatEventDate(event.start_time)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {!hasResults && (
                            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Searcher;
